import { useCallback, useEffect, useRef } from 'react';

/** Hold this long on a Worktop icon to enter rearrange mode. */
export const LONG_PRESS_MS = 500;

/** Pointer travel (px) that turns a press into a scroll/drag and cancels it. */
const MOVE_TOLERANCE = 8;

/**
 * Press-and-hold detector.
 *
 * Fires `onLongPress` once per press, after {@link LONG_PRESS_MS}, and cancels
 * on release, on a pointer that travels more than {@link MOVE_TOLERANCE}, and on
 * pointer cancel/leave — so a tap still activates the element and a flick still
 * scrolls the page. `firedRef` lets the caller swallow the click that ends a
 * press which became a long press.
 */
export function useLongPress(onLongPress: () => void, enabled: boolean) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const origin = useRef<{ x: number; y: number } | null>(null);
  const firedRef = useRef(false);

  const clear = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    origin.current = null;
  }, []);

  useEffect(() => clear, [clear]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!enabled || e.button !== 0) return;
      firedRef.current = false;
      origin.current = { x: e.clientX, y: e.clientY };
      timer.current = setTimeout(() => {
        firedRef.current = true;
        clear();
        onLongPress();
      }, LONG_PRESS_MS);
    },
    [enabled, onLongPress, clear],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const start = origin.current;
      if (!start) return;
      if (
        Math.abs(e.clientX - start.x) > MOVE_TOLERANCE ||
        Math.abs(e.clientY - start.y) > MOVE_TOLERANCE
      ) {
        clear();
      }
    },
    [clear],
  );

  return {
    /** True when the press that just ended became a long press. */
    firedRef,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: clear,
      onPointerCancel: clear,
      onPointerLeave: clear,
    },
  };
}
